/* @jsx h */

import {
  fetchProducts,
  fetchPromotions,
  fetchServices,
  fetchShop,
} from "@app/server/client";
import {
  Category,
  GroupOrder,
  Product,
  Promotion,
  Service,
  ShopProfile,
  SelectedEvent,
} from "@app/types/index";
import { useCart } from "@lemmonhq/orders";
import {
  defineComponent,
  inject,
  onMounted,
  provide,
  ref,
  Ref,
  watch,
  computed,
  h,
  onUnmounted,
  reactive,
} from "vue";
import { useRoute } from "vue-router";
import { useRealtime } from "./realtime/index";
import {
  filterServicesBySchedule,
  getRestaurantLogoURL,
  pp,
} from "@lemmonhq/blogic";
import { useCustomer } from "../Auth";

import { debounce } from "@lemmonhq/orders";
import {
  checkOpeningHours,
  getLastTableIdKey,
  getServiceTypeForMethod,
  extractCategoriesFromServices,
  filterProductsByCategories,
  filterAvailableProducts,
  performMenuSearch,
} from "./utils";
import { fetchGroupOrder } from "@app/server/client";

// Keys for provide/inject
const MenuContextKey = Symbol("MenuContext");

interface ReservationFormState {
  partySize: number;
  selectedDate: Date | null;
  selectedService: string | null;
  selectedTime: string;
  name: string;
  email: string;
  phone: string;
  notes: string;
  selectedTagIds: string[];
  isSubmitting: boolean;
  submitError: string | null;
}

interface MenuContextType {
  products: Ref<Product[]>;
  services: Ref<Service[]>;
  groupOrder: Ref<GroupOrder | null>;
  categories: Ref<Category[]>;
  allProducts: Ref<Product[]>;
  allServices: Ref<Service[]>;
  allCategories: Ref<Category[]>;
  shop: Ref<ShopProfile>;
  cart: ReturnType<typeof useCart>;
  realtime: ReturnType<typeof useRealtime>;
  searchQuery: Ref<string>;
  promotions: Ref<Promotion[]>;
  isRestaurantOpen: Ref<boolean>;
  getLastTableId: () => string | null;
  refetchGroupOrder: () => Promise<GroupOrder | undefined>;
  selectedEvent: Ref<SelectedEvent | undefined>;
  reservationForm: ReservationFormState;
  resetReservationForm: () => void;
}

export type MenuProviderProps = {
  render: Function;
};
export const MenuProvider = defineComponent<MenuProviderProps>({
  name: "MenuProvider",
  props: ["render"],
  setup(props, { slots }) {
    const loaded = ref(false);
    const groupOrder = ref<GroupOrder>();
    const allProducts = ref<Product[]>([]);
    const allServices = ref<Service[]>([]);
    const allCategories = ref<Category[]>([]);
    const promotions = ref<Promotion[]>([]);
    const shop = ref<ShopProfile | undefined>();
    const cart = useCart();
    const customer = useCustomer();
    const route = useRoute();
    const shopLoaded = ref(false);
    const searchQuery = ref("");
    const selectedEvent = ref<SelectedEvent | undefined>();

    const realtime = useRealtime(customer, cart);
    const isRestaurantOpen = ref(true);

    // Reservation form state
    const reservationForm = reactive<ReservationFormState>({
      partySize: 2,
      selectedDate: null,
      selectedService: null,
      selectedTime: "",
      name: "",
      email: "",
      phone: "",
      notes: "",
      selectedTagIds: [],
      isSubmitting: false,
      submitError: null,
    });

    const resetReservationForm = () => {
      reservationForm.partySize = 2;
      reservationForm.selectedDate = null;
      reservationForm.selectedService = null;
      reservationForm.selectedTime = "";
      reservationForm.name = "";
      reservationForm.email = "";
      reservationForm.phone = "";
      reservationForm.notes = "";
      reservationForm.selectedTagIds = [];
      reservationForm.isSubmitting = false;
      reservationForm.submitError = null;
    };

    const setupShop = async (
      fetchedShop: ShopProfile,
      groupOrder: GroupOrder,
    ) => {
      try {
        await cart.setupShop(fetchedShop, groupOrder.id);
        console.log(
          "Shop setup completed for:",
          fetchedShop.id,
          "with groupOrder:",
          groupOrder.id,
        );
      } catch (error) {
        console.error("Failed to setup shop, retrying:", error);
        setTimeout(() => {
          cart.setupShop(fetchedShop, groupOrder.id);
        }, 1000);
      }
    };

    // Use computed to ensure both shop and groupOrder are available before setup
    const setupShopWhenReady = computed(() => {
      return shop.value && groupOrder.value;
    });

    watch(
      setupShopWhenReady,
      async (isReady) => {
        if (isReady && shop.value && groupOrder.value) {
          await setupShop(shop.value, groupOrder.value);
        }
      },
      { immediate: true },
    );

    watch(
      groupOrder,
      () => {
        // Initialize cart.serviceMethod if not set
        if (groupOrder.value?.table_id) {
          cart.cart.serviceMethod = "dine-in";
        } else {
        }
        console.log("cart.cart.serviceMethod", cart.cart.serviceMethod);
      },
      { immediate: true },
    );

    //    watch(
    //      groupOrder,
    //      () => {
    //        if (
    //          groupOrder.value &&
    //          realtime.channelId.value !== groupOrder.value.id &&
    //          groupOrder.value.status == "active"
    //        ) {
    //          realtime.channelId.value = groupOrder.value.id;
    //          if (groupOrder.value.id) {
    //            realtime.enable();
    //          }
    //        }
    //        if (groupOrder.value?.status && groupOrder.value?.status !== "active") {
    //          realtime.disable();
    //        }
    //      },
    //      { immediate: true }
    //    );

    // Watch for ORDER_CONFIRMED or ORDER_FINALIZED events to refresh group order
    watch(
      () => realtime.eventLog.value,
      async (events) => {
        if (!events || events.length === 0) return;

        const latestEvent = events[events.length - 1];
        if (
          latestEvent?.type === "ORDER_CONFIRMED" ||
          latestEvent?.type === "ORDER_FINALIZED"
        ) {
          refetchGroupOrder();
        }
      },
    );

    const refetchGroupOrder = async () => {
      if (groupOrder.value?.id && shop.value) {
        try {
          const updatedGroupOrder = await fetchGroupOrder(groupOrder.value.id);
          groupOrder.value = updatedGroupOrder;
          console.log("refetchGroupOrder", updatedGroupOrder);
          return updatedGroupOrder;
        } catch (error) {
          console.error("Failed to fetch updated group order:", error);
        }
      }
    };

    // Computed variables based on service method
    const services = computed(() => {
      const serviceMethod = cart.cart.serviceMethod;
      if (!serviceMethod) return allServices.value;

      const requiredServiceType = getServiceTypeForMethod(serviceMethod);
      if (!requiredServiceType) return allServices.value;

      const list = filterServicesBySchedule(
        allServices.value.filter(
          (service) =>
            service.service_types?.includes(requiredServiceType) &&
            !service.visible_only_to_staff,
        ),
      );
      console.log({ list, requiredServiceType, serviceMethod });
      return list;
    });
    const categories = computed(() => {
      return extractCategoriesFromServices(services.value);
    });
    const products = computed(() => {
      return filterProductsByCategories(
        allProducts.value,
        categories.value,
        allCategories.value,
      );
    });

    // Filtered data based on search query
    const filteredProducts = ref<Product[]>([]);
    const filteredServices = ref<Service[]>([]);
    const filteredCategories = ref<Category[]>([]);
    const isSearching = ref(false);

    // Debounced search function
    const debouncedSearch = debounce((query: string) => {
      const searchResult = performMenuSearch(
        query,
        products.value,
        categories.value,
        services.value,
      );

      isSearching.value = searchResult.isSearching;
      filteredProducts.value = searchResult.products;
      filteredServices.value = searchResult.services;
      filteredCategories.value = searchResult.categories;

      console.log("filteredProducts", filteredProducts.value);
    }, 300);
    watch(searchQuery, (newQuery) => {
      debouncedSearch(newQuery);
    });

    // Final computed variables that combine search state
    const finalProducts = computed(() => {
      return isSearching.value ? filteredProducts.value : products.value;
    });

    const finalServices = computed(() => {
      return isSearching.value ? filteredServices.value : services.value;
    });

    const finalCategories = computed(() => {
      return isSearching.value ? filteredCategories.value : categories.value;
    });

    // Set up interval to check opening hours every 5 seconds
    let isRestaurantOpenInterval: NodeJS.Timeout;
    onMounted(() => {
      isRestaurantOpenInterval = setInterval(() => {
        if (loaded.value && shop.value) {
          isRestaurantOpen.value = checkOpeningHours(shop.value);
        }
      }, 5000);
    });
    onUnmounted(
      () => isRestaurantOpenInterval && clearInterval(isRestaurantOpenInterval),
    );

    const fetchData = async () => {
      console.log(route);
      const params = route.params as { shopslug: string; tableslug: string };
      if (!params.shopslug) {
        return;
      }
      const promises: [
        Promise<Service[]>,
        Promise<Product[]>,
        Promise<Promotion[]>,
      ] = [
        fetchServices(params.shopslug),
        fetchProducts(params.shopslug),
        fetchPromotions(params.shopslug),
      ];

      const fetchedShop = await fetchShop(params.shopslug);

      shop.value = fetchedShop as any;
      shopLoaded.value = true;

      const [fetchedServices, fetchedProducts, fetchedPromotions] =
        await Promise.all(promises);
      console.log({ fetchedProducts, fetchedServices });

      // Store raw data with minimal filtering (only availability and deletion status)
      const availableProducts = filterAvailableProducts(
        fetchedProducts as Product[],
      );

      // Store all services without filtering by service type
      const availableServices = fetchedServices as Service[];

      // Get all categories from all services
      const availableCategories =
        extractCategoriesFromServices(availableServices);

      // Store raw data
      allProducts.value = pp(availableProducts);
      allServices.value = pp(availableServices);
      allCategories.value = pp(availableCategories);
      promotions.value = fetchedPromotions;
      isRestaurantOpen.value = checkOpeningHours(fetchedShop);

      loaded.value = true;
    };

    onMounted(() => {
      fetchData();
    });

    const handleWindowBlur = async () => {
      await realtime.disable();
    };

    const handleWindowFocus = async () => {
      if (groupOrder.value) {
        await realtime.enable(groupOrder.value);
      }
    };

    onMounted(() => {
      if (!import.meta.env.VITE_LOCALHOST) {
        if (groupOrder.value && groupOrder.value.status === "active") {
          window.addEventListener("blur", handleWindowBlur);
          window.addEventListener("focus", handleWindowFocus);
        }
      }
    });

    onUnmounted(() => {
      if (!import.meta.env.VITE_LOCALHOST) {
        if (groupOrder.value && groupOrder.value.status === "active") {
          window.removeEventListener("blur", handleWindowBlur);
          window.removeEventListener("focus", handleWindowFocus);
        }
      }
    });

    provide(MenuContextKey, {
      products: finalProducts,
      services: finalServices,
      shop,
      cart,
      realtime,
      categories: finalCategories,
      allProducts,
      allServices,
      allCategories,
      searchQuery,
      promotions,
      isRestaurantOpen,
      groupOrder,
      refetchGroupOrder,
      selectedEvent,
      reservationForm,
      resetReservationForm,
      getLastTableId: () => {
        if (shop.value) {
          return localStorage.getItem(getLastTableIdKey(shop.value));
        }
      },
    });

    // Render the children passed to AuthProvider
    return () => {
      return loaded.value ? (
        <>{slots.default?.()}</>
      ) : shop.value ? (
        <div
          style="padding-top: 80px;"
          class="flex flex-1 justify-center items-center h-screen w-screen"
        >
          <div
            data-image={`${getRestaurantLogoURL(shop.value)}`}
            style={`background-image: url("${getRestaurantLogoURL(
              shop.value,
            )}");`}
            class="flex-1 bg-no-repeat bg-contain bg-center h-full max-w-[300px]"
          ></div>
        </div>
      ) : (
        <div class="w-full h-screen flex items-center justify-center">
          <div class="loading" />
        </div>
      );
    };
  },
});

// Hook to use Auth
export function useMenu(): MenuContextType {
  const context = inject<MenuContextType>(MenuContextKey);
  if (!context) {
    throw new Error("useMenu must be used within an MenuProvider");
  }
  return context;
}

export function useShop() {
  return useMenu().shop;
}
